import React, { useRef, useState, useEffect, useMemo } from 'react';
import { TrendingUp, Heart, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';

import { withVersion } from '../../utils/assetVersion';

const STEPS = [
    {
        time: "SEMAINE 1",
        title: "L'EFFET WHAOU",
        desc: "Vos premiers clients reçoivent leurs colis. Le parfum embaume la pièce dès l'ouverture. Ils vous envoient des DM pour demander : 'C'est quoi ce parfum ?!'",
        icon: <Heart className="w-8 h-8 text-[#ff0050]" />,
        color: "border-[#ff0050]",
        image: withVersion("/assets/croissance-ventes-emballage-premium-stats.webp")
    },
    {
        time: "MOIS 1",
        title: "LA FIDÉLISATION",
        desc: "Vos évaluations 5 étoiles explosent. Les clients mentionnent spécifiquement 'l'emballage soigné' et 'l'odeur incroyable' dans leurs avis Vinted et TikTok Shop.",
        icon: <TrendingUp className="w-8 h-8 text-[#00f2ea]" />,
        color: "border-[#00f2ea]",
        image: withVersion('/assets/Assis1.jpg')
    },
    {
        time: "MOIS 3",
        title: "LA VIRALITÉ",
        desc: "Vos clients postent leurs unboxings sur TikTok. L'algorithme repère les couleurs fluo et le design unique. Vos vues et vos ventes décollent organiquement.",
        icon: <Share2 className="w-8 h-8 text-[#ffee00]" />,
        color: "border-[#ffee00]",
        image: withVersion("/assets/augmentation-commandes-sac-vinted-succes.webp")
    }
];

const GrowthTimeline = () => {
    const [activeCardIndex, setActiveCardIndex] = useState(-1);
    const [isDesktop, setIsDesktop] = useState(false);

    // Initialize state dynamically based on STEPS length
    const [manualFlipState, setManualFlipState] = useState(() => new Array(STEPS.length).fill(false));
    const sectionRef = useRef(null);
    const cardRefs = useRef([]);

    useEffect(() => {
        const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
        checkDesktop();
        window.addEventListener('resize', checkDesktop);
        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    // Handle manual card interaction
    const handleCardInteraction = (index) => {
        if (!isDesktop) {
            // Mobile: Click toggles flip (keep existing behavior if needed, or rely on scroll)
            setManualFlipState(prev => {
                const newState = [...prev];
                newState[index] = !newState[index];
                return newState;
            });
        }
    };

    const handleHover = (index, isEntering) => {
        if (isDesktop) {
            setActiveCardIndex(isEntering ? index : -1);
        }
    };

    useEffect(() => {
        // Mobile-only Observer
        if (isDesktop) return;

        // Options pour l'observateur: déclenche quand 50% de la carte est visible
        const options = {
            root: null,
            rootMargin: '-20% 0px -20% 0px', // Marge de sécurité pour centrer l'activation
            threshold: 0.5
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // Retrouver l'index de la carte observée
                    const index = cardRefs.current.findIndex(ref => ref === entry.target);

                    if (index !== -1) {
                        // Activer cette carte (et désactiver les autres implicitement via le state)
                        setActiveCardIndex(index);
                    }
                }
            });
        }, options);

        // Observer chaque carte
        cardRefs.current.forEach((card) => {
            if (card) observer.observe(card);
        });

        return () => {
            if (cardRefs.current) {
                cardRefs.current.forEach((card) => {
                    if (card) observer.unobserve(card);
                });
            }
        };
    }, [isDesktop]); // Re-run if desktop state changes

    // Determine if a card should be flipped (either by IO or manual click)
    const isCardFlipped = (index) => {
        // Desktop: Hover state (managed via activeCardIndex)
        if (isDesktop) {
            return activeCardIndex === index;
        }

        // Mobile: Manual flip takes priority
        if (manualFlipState[index]) {
            return activeCardIndex !== index;
        }
        // Mobile: Scroll-based flip
        return activeCardIndex === index;
    };

    return (
        <section ref={sectionRef} className="py-24 px-6 bg-black text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(#333_1px,transparent_1px)] [background-size:16px_16px] opacity-20"></div>

            <div className="max-w-6xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-6xl font-black font-display mb-4">VOTRE TRAJECTOIRE DE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f2ea] to-white">CROISSANCE</span></h2>
                    <p className="text-xl text-gray-400">Ce n'est pas juste un sac. C'est votre futur business plan.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden md:block absolute top-12 left-0 w-full h-1 bg-gray-800 -z-10"></div>

                    {STEPS.map((step, i) => (
                        <div
                            key={i}
                            ref={el => cardRefs.current[i] = el}
                            className="perspective-1000 h-[350px] md:h-[320px] cursor-pointer"
                            onClick={() => handleCardInteraction(i)}
                            onMouseEnter={() => handleHover(i, true)}
                            onMouseLeave={() => handleHover(i, false)}
                        >
                            <motion.div
                                className="relative w-full h-full"
                                style={{ transformStyle: 'preserve-3d' }}
                                animate={{ rotateY: isCardFlipped(i) ? 180 : 0 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 25 }}
                            >
                                {/* FRONT FACE - Text Content */}
                                <div
                                    className={`absolute inset-0 bg-[#111] border-t-4 ${step.color} p-8 rounded-2xl backface-hidden`}
                                    style={{ backfaceVisibility: 'hidden' }}
                                >
                                    <div className="w-16 h-16 bg-black border-4 border-white rounded-full flex items-center justify-center mb-6 mx-auto relative z-10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                                        {step.icon}
                                    </div>
                                    <div className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2">{step.time}</div>
                                    <h3 className="text-2xl font-black mb-4">{step.title}</h3>
                                    <p className="text-gray-400 font-medium leading-relaxed text-sm">
                                        {step.desc}
                                    </p>
                                </div>

                                {/* BACK FACE - Image */}
                                <div
                                    className={`absolute inset-0 rounded-2xl overflow-hidden border-t-4 ${step.color} backface-hidden`}
                                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                >
                                    <img
                                        src={step.image}
                                        alt={step.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">{step.time}</div>
                                        <h3 className="text-xl font-black text-white">{step.title}</h3>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default GrowthTimeline;

